import type { ContactOrderStep } from "../../../../../../../../../../api/kehrblatt/contact-orders"
import {
  contactPointOriginPresentation,
  sortContactPointsByQuality,
} from "../../../../../../../../_shared/_logic/contact-quality-tags"
import type { ContactStepTargetOption } from "../_shared/_logic/step-editor-contract"
import { isPhoneContactPointKind } from "../../../../../../../../_shared/_logic/contact-point-kinds"

export const readTargetFromStep = (step?: ContactOrderStep | null) =>
  step?.destination?.contact_point_id ?? null

export const buildCustomerById = (customers: Customer[] = []) =>
  Object.fromEntries((customers ?? []).map((customer) => [customer.id, customer]))

export const sortTargetsByQuality = (contactPoints: ContactPoint[] = []) =>
  sortContactPointsByQuality(contactPoints)

export const formatTargetLabel = (cp: ContactPoint, customerById: Record<string, Customer>) => {
  const customerName = cp.customer_id ? customerById[cp.customer_id]?.display_name : null
  const attrs: string[] = []

  if (cp.is_verified) attrs.push("VERIFIZIERT")

  const source = contactPointOriginPresentation(cp).label

  if (source) attrs.push(source.toUpperCase())

  const prefix = customerName ? `${customerName} - ` : ""
  const baseLabel = cp.value || cp.label || ""

  return `${prefix}${baseLabel}${attrs.length ? ` (${attrs.join(" & ")})` : ""}`
}

export const splitContactTargets = (contactPoints: ContactPoint[] = []) => {
  const prioritized = sortTargetsByQuality(contactPoints)
  const emailTargets = prioritized.filter((cp) => cp.kind === "EMAIL")
  const phoneTargets = prioritized.filter((cp) => isPhoneContactPointKind(cp.kind))
  const mobilePhoneTargets = phoneTargets.filter((cp) => cp.kind === "MOBILE")

  return { prioritized, emailTargets, phoneTargets, mobilePhoneTargets }
}

export const defaultTargetsFor = (
  channel: ContactChannel | "",
  targetPools: {
    emailTargets: ContactPoint[]
    phoneTargets: ContactPoint[]
    mobilePhoneTargets: ContactPoint[]
  },
) => {
  if (channel === "EMAIL") return targetPools.emailTargets.map((cp) => cp.id)

  if (channel === "MOBILE_MESSAGE") return targetPools.mobilePhoneTargets.map((cp) => cp.id)

  if (channel === "PHONE") return targetPools.phoneTargets.map((cp) => cp.id)

  return []
}

export const sanitizeTargets = (
  channel: ContactChannel | "",
  targetIds: string[] | null | undefined,
  targetPools: {
    emailTargets: ContactPoint[]
    phoneTargets: ContactPoint[]
    mobilePhoneTargets: ContactPoint[]
  },
) => {
  const pool =
    channel === "EMAIL"
      ? targetPools.emailTargets
      : channel === "MOBILE_MESSAGE"
        ? targetPools.mobilePhoneTargets
        : channel === "PHONE"
          ? targetPools.phoneTargets
          : []
  const validIds = pool.map((cp) => cp.id)

  if (!validIds.length) return []

  const normalized = (targetIds ?? []).filter((targetId) => validIds.includes(targetId))

  return normalized
}

export const buildTargetOptions = (
  channel: ContactChannel | "",
  targetPools: {
    emailTargets: ContactPoint[]
    phoneTargets: ContactPoint[]
  },
  customerById: Record<string, Customer>,
): ContactStepTargetOption[] => {
  if (channel === "EMAIL") {
    return targetPools.emailTargets.map((cp) => ({
      name: formatTargetLabel(cp, customerById),
      value: cp.id,
    }))
  }

  if (channel === "MOBILE_MESSAGE") {
    return targetPools.phoneTargets.map((cp) => ({
      name: formatTargetLabel(cp, customerById),
      value: cp.id,
      selectable: cp.kind === "MOBILE",
    }))
  }

  if (channel === "PHONE") {
    return targetPools.phoneTargets.map((cp) => ({
      name: formatTargetLabel(cp, customerById),
      value: cp.id,
    }))
  }

  return []
}
